/*! product-media-reinit.js — canonical single patch */
/*
  Keeps gallery openers and ProductModal media IDs aligned when the product gallery is re-rendered.
  The key guard here is "prefer the visible gallery" because duplicate galleries can be present during
  section rendering and Dawn's default selectors may bind to the wrong one.
*/
(function () {
  var OPENER_SEL = '.product__media-toggle[data-media-id]';
  var MODAL_OPENER_SEL = 'modal-opener[data-modal^="#ProductModal-"]';
  var GALLERY_SEL = 'media-gallery[id^="MediaGallery-"]';
  var VARIANT_EVENTS = [
    'variant:changed', 'variantChanged', 'variantChange', 'product:variant-change',
    'shopify:section:load'
  ];

  var sectionState = Object.create(null);
  var observedGalleries = new WeakSet();
  var mutationFlushRaf = 0;
  var pendingMutationSectionIds = new Set();
  var visibleElementCache = Object.create(null);
  var observedProductModals = new WeakSet();
  var currentOpenProductModal = null;
  var activationState = {
    active: false,
    bound: false
  };

  // ---------- visibility / section state ----------
  function resetVisibleElementCache() {
    visibleElementCache = Object.create(null);
  }

  function isVisibleElement(el) {
    if (!el || !el.isConnected) return false;
    if (el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
    if (el.classList && (el.classList.contains('hidden') || el.classList.contains('swatch-hide'))) return false;
    // Hidden duplicate galleries are common here, so avoid layout-heavy checks until the cheap guards pass.
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function getVisibleElementById(id) {
    if (!id) return null;
    var cached = visibleElementCache[id];
    if (cached && cached.isConnected && isVisibleElement(cached)) return cached;

    var first = document.getElementById(id);
    if (!first) return null;
    if (isVisibleElement(first)) {
      visibleElementCache[id] = first;
      return first;
    }

    var candidates = document.querySelectorAll('[id="' + id + '"]');
    if (!candidates.length) return null;
    var picked = Array.prototype.find.call(candidates, isVisibleElement) || null;
    if (picked) {
      visibleElementCache[id] = picked;
      return picked;
    }

    return first || candidates[0] || null;
  }

  function getVisibleGalleryList(sectionId) {
    return getVisibleElementById('Slider-Gallery-' + sectionId);
  }

  function getState(sectionId) {
    if (!sectionId) return null;
    if (!sectionState[sectionId]) {
      sectionState[sectionId] = {
        modalIds: null,
        exactSet: null,
        bySuffix: Object.create(null),
        syncRaf: 0,
        syncTimer: 0,
        filterRaf: 0,
        lastVisibleSignature: ''
      };
    }
    return sectionState[sectionId];
  }

  function hasProductMediaContext() {
    return !!(
      document.querySelector(GALLERY_SEL) ||
      document.querySelector('[id^="ProductInfo-"]') ||
      document.querySelector('[id^="ProductModal-"]')
    );
  }

  function isOpenProductModal(modal) {
    return !!(
      modal &&
      modal.isConnected &&
      modal.matches &&
      modal.matches('product-modal[open], product-modal[aria-hidden="false"]')
    );
  }

  function rememberOpenProductModal(modal) {
    currentOpenProductModal = isOpenProductModal(modal) ? modal : null;
    return currentOpenProductModal;
  }

  function forgetOpenProductModal(modal) {
    if (!modal || currentOpenProductModal === modal) currentOpenProductModal = null;
  }

  function getCachedOpenProductModal() {
    if (isOpenProductModal(currentOpenProductModal)) return currentOpenProductModal;
    currentOpenProductModal = null;
    return null;
  }

  function getOpenProductModal() {
    var cached = getCachedOpenProductModal();
    if (cached) return cached;

    var modal = document.querySelector('product-modal[open], product-modal[aria-hidden="false"]');
    if (modal) observeProductModalNode(modal);
    return rememberOpenProductModal(modal);
  }

  function syncOpenProductModalCache(modal) {
    if (isOpenProductModal(modal)) {
      currentOpenProductModal = modal;
    } else if (currentOpenProductModal === modal) {
      currentOpenProductModal = null;
    }
  }

  var productModalAttributeObserver = new MutationObserver(function (muts) {
    muts.forEach(function (m) {
      syncOpenProductModalCache(m.target);
    });
  });

  function observeProductModalNode(modal) {
    if (!modal || !modal.matches || !modal.matches('product-modal[id^="ProductModal-"]')) return;
    if (!observedProductModals.has(modal)) {
      observedProductModals.add(modal);
      productModalAttributeObserver.observe(modal, {
        attributes: true,
        attributeFilter: ['open', 'aria-hidden']
      });
    }
    syncOpenProductModalCache(modal);
  }

  function observeProductModals() {
    document.querySelectorAll('product-modal[id^="ProductModal-"]').forEach(observeProductModalNode);
  }

  // ---------- media ID mapping ----------
  function getSectionId(el) {
    var opener = el.closest && el.closest('modal-opener');
    if (opener) {
      var sel = opener.getAttribute('data-modal') || '';
      if (sel.indexOf('#ProductModal-') === 0) return sel.slice('#ProductModal-'.length);
    }

    var gal = el.closest && el.closest(GALLERY_SEL);
    if (gal && gal.id) return gal.id.replace('MediaGallery-', '');

    var info = el.closest && el.closest('[id^="ProductInfo-"]');
    if (info && info.id) return info.id.replace('ProductInfo-', '');

    return null;
  }

  function clickedGalleryMediaId(el) {
    var item = el && el.closest && el.closest('.product__media-item[data-media-id]');
    return item ? item.getAttribute('data-media-id') : null;
  }

  function firstVisibleGalleryId(sectionId) {
    var list = getVisibleGalleryList(sectionId);
    if (!list) return null;

    var li = Array.prototype.find.call(
      list.querySelectorAll('.product__media-item[data-media-id]'),
      function (n) {
        var hidden =
          n.classList.contains('hidden') ||
          n.classList.contains('swatch-hide') ||
          n.getAttribute('aria-hidden') === 'true' ||
          (n.style && (n.style.display === 'none' || n.style.visibility === 'hidden'));
        return !hidden;
      }
    );

    return li ? li.getAttribute('data-media-id') : null;
  }

  function rebuildModalIndex(sectionId) {
    var st = getState(sectionId);
    if (!st) return;

    var modal = document.getElementById('ProductModal-' + sectionId);
    var ids = modal
      ? Array.prototype.map.call(modal.querySelectorAll('[data-media-id]'), function (n) {
          return n.getAttribute('data-media-id');
        }).filter(Boolean)
      : [];

    st.modalIds = ids;
    st.exactSet = new Set(ids);
    st.bySuffix = Object.create(null);
    ids.forEach(function (id) {
      var suf = String(id).split('-').pop();
      if (suf && !st.bySuffix[suf]) st.bySuffix[suf] = id;
    });
  }

  function mapToExistingModalId(sectionId, candidateId) {
    var st = getState(sectionId);
    if (!st) return null;
    if (!st.modalIds) rebuildModalIndex(sectionId);
    if (!st.modalIds || !st.modalIds.length) return null;

    if (candidateId && st.exactSet && st.exactSet.has(candidateId)) return candidateId;
    var suf = candidateId ? String(candidateId).split('-').pop() : '';
    return (suf && st.bySuffix && st.bySuffix[suf]) || null;
  }

  function normalizeImageKey(url) {
    if (!url) return '';
    try {
      var abs = new URL(url, location.href);
      return abs.pathname;
    } catch (_) {
      return String(url).split('?')[0];
    }
  }

  function mapByActiveImageSrc(sectionId, sourceEl) {
    var modal = document.getElementById('ProductModal-' + sectionId);
    if (!modal) return null;

    var visibleList = getVisibleGalleryList(sectionId);
    var activeItem =
      (sourceEl && sourceEl.closest && sourceEl.closest('.product__media-item')) ||
      (visibleList && visibleList.querySelector('.product__media-item.is-active'));
    if (!activeItem) return null;

    var img =
      activeItem.querySelector('.product__modal-opener--image img') ||
      activeItem.querySelector('.product__media img');
    var key = normalizeImageKey(img && (img.currentSrc || img.src));
    if (!key) return null;

    var node = Array.prototype.find.call(
      modal.querySelectorAll('[data-media-id]'),
      function (n) {
        var modalImg =
          (n.matches && n.matches('img[data-media-id]') && n) ||
          n.querySelector && (n.querySelector('img[data-media-id]') || n.querySelector('img'));
        if (!modalImg) return false;
        var modalKey = normalizeImageKey(modalImg.currentSrc || modalImg.src);
        return modalKey && modalKey === key;
      }
    );

    return node ? node.getAttribute('data-media-id') : null;
  }

  function resolveModalMediaId(sectionId, btn, clickTarget) {
    var desired =
      clickedGalleryMediaId(clickTarget) ||
      clickedGalleryMediaId(btn) ||
      btn.dataset.mediaId ||
      firstVisibleGalleryId(sectionId);

    return mapToExistingModalId(sectionId, desired) ||
      mapToExistingModalId(sectionId, firstVisibleGalleryId(sectionId)) ||
      mapByActiveImageSrc(sectionId, btn);
  }

  // ---------- gallery visibility sync ----------
  function visibleMediaIdSet(sectionId) {
    var ul = getVisibleGalleryList(sectionId);
    var ids = new Set();
    if (!ul) return ids;

    ul.querySelectorAll('.product__media-item[data-media-id]').forEach(function (li) {
      var hidden =
        li.classList.contains('hidden') ||
        li.classList.contains('swatch-hide') ||
        li.getAttribute('aria-hidden') === 'true' ||
        (li.style && (li.style.display === 'none' || li.style.visibility === 'hidden'));
      if (!hidden) {
        var id = li.getAttribute('data-media-id');
        if (id) ids.add(id);
      }
    });

    return ids;
  }

  function syncOpenersToModal(sectionId) {
    var ul = getVisibleGalleryList(sectionId);
    if (!ul) return;

    ul.querySelectorAll('.product__media-item[data-media-id]').forEach(function (item) {
      var btn = item.querySelector('.product__media-toggle[data-media-id]');
      if (!btn) return;

      var galleryId = item.getAttribute('data-media-id') || '';
      var mapped =
        mapToExistingModalId(sectionId, galleryId) ||
        mapToExistingModalId(sectionId, btn.dataset.mediaId);
      if (mapped) btn.dataset.mediaId = mapped;
    });
  }

  function runSync(sectionId) {
    rebuildModalIndex(sectionId);
    syncOpenersToModal(sectionId);
  }

  function scheduleSync(sectionId) {
    var st = getState(sectionId);
    if (!st) return;

    // Apps can rewrite media twice in quick succession; RAF catches the paint-cycle update, timer catches late DOM swaps.
    if (!st.syncRaf) {
      st.syncRaf = requestAnimationFrame(function () {
        st.syncRaf = 0;
        runSync(sectionId);
      });
    }

    if (!st.syncTimer) {
      st.syncTimer = setTimeout(function () {
        st.syncTimer = 0;
        runSync(sectionId);
      }, 80);
    }
  }

  // ---------- modal filtering ----------
  function filterModalToVisible(sectionId) {
    var modal = document.getElementById('ProductModal-' + sectionId);
    if (!modal) return;

    var st = getState(sectionId);
    if (!st) return;

    var visible = visibleMediaIdSet(sectionId);
    if (!visible.size) return; // Avoid blanking the modal during transient all-hidden states.

    var visibleIds = Array.from(visible);
    var visibleSignature = visibleIds.slice().sort().join('|');
    if (st.lastVisibleSignature === visibleSignature) return;
    st.lastVisibleSignature = visibleSignature;

    var visibleSuffixes = new Set(
      visibleIds
        .map(function (id) { return String(id).split('-').pop(); })
        .filter(Boolean)
    );

    modal.querySelectorAll('[data-media-id]').forEach(function (node) {
      var id = node.getAttribute('data-media-id') || '';
      var suffix = id.indexOf('-') !== -1 ? id.split('-').pop() : id;
      var ok =
        visible.has(id) ||
        visibleSuffixes.has(suffix);
      node.style.display = ok ? '' : 'none';
    });
  }

  function scheduleFilter(sectionId) {
    var st = getState(sectionId);
    if (!st || st.filterRaf) return;

    st.filterRaf = requestAnimationFrame(function () {
      st.filterRaf = 0;
      filterModalToVisible(sectionId);
    });
  }

  // ---------- activation ----------
  function activateProductMediaReinit() {
    if (activationState.active) return;
    if (!hasProductMediaContext()) return;

    activationState.active = true;
    resetVisibleElementCache();
    observeProductModals();
    observeGalleries();
    document.querySelectorAll(GALLERY_SEL).forEach(function (gal) {
      var sid = gal.id && gal.id.replace('MediaGallery-', '');
      if (!sid) return;
      scheduleSync(sid);
    });
  }

  function bindActivationHooks() {
    if (activationState.bound) return;
    activationState.bound = true;

    var schedulePostInteractionActivation = function () {
      requestAnimationFrame(function () {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(activateProductMediaReinit, { timeout: 1200 });
        } else {
          setTimeout(activateProductMediaReinit, 250);
        }
      });
    };

    var onFirstIntent = function () {
      schedulePostInteractionActivation();
    };

    ['pointerdown', 'touchstart', 'keydown'].forEach(function (evt) {
      document.addEventListener(evt, onFirstIntent, { passive: true, once: true, capture: true });
    });

    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(activateProductMediaReinit, 1500);
    });

    window.addEventListener('load', function () {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(activateProductMediaReinit, { timeout: 1200 });
      } else {
        setTimeout(activateProductMediaReinit, 600);
      }
    }, { once: true });
  }

  // ---------- event binding ----------
  function handleMediaClick(ev) {
    if (!activationState.active) activateProductMediaReinit();

    var clickTarget = ev.target && ev.target.closest
      ? ev.target.closest(OPENER_SEL + ', ' + MODAL_OPENER_SEL)
      : null;
    if (!clickTarget) return;

    var btn = clickTarget.matches && clickTarget.matches(OPENER_SEL)
      ? clickTarget
      : clickTarget.querySelector && clickTarget.querySelector(OPENER_SEL);
    if (!btn) return;

    try {
      var sid = getSectionId(btn);
      if (!sid) return;

      var mapped = resolveModalMediaId(sid, btn, clickTarget);
      if (mapped) {
        btn.dataset.mediaId = mapped;
      } else {
        var first = document.querySelector('#ProductModal-' + sid + ' [data-media-id]');
        if (first) btn.dataset.mediaId = first.getAttribute('data-media-id');
      }

      filterModalToVisible(sid);
      // Some theme/app code replaces modal nodes after click handling, so reapply on the next frame.
      scheduleFilter(sid);
    } catch (e) {
      return;
    }
  }

  function handleVariantEvent(event) {
    if (!activationState.active) return;
    if (!hasProductMediaContext()) return;

    resetVisibleElementCache();
    var sidFromEvent = event && event.target && getSectionId(event.target);
    if (sidFromEvent) {
      scheduleSync(sidFromEvent);
    } else {
      document.querySelectorAll(GALLERY_SEL).forEach(function (gal) {
        var sid = gal.id && gal.id.replace('MediaGallery-', '');
        if (!sid) return;
        scheduleSync(sid);
      });
    }

    var openModal = getOpenProductModal();
    if (!openModal || !openModal.id || openModal.id.indexOf('ProductModal-') !== 0) return;
    scheduleFilter(openModal.id.replace('ProductModal-', ''));
  }

  function bindEventHandlers() {
    // Capture runs before Dawn opens the modal, letting us normalize data-media-id while the opener is still the source of truth.
    document.addEventListener('click', handleMediaClick, true);

    VARIANT_EVENTS.forEach(function (ev) {
      document.addEventListener(ev, handleVariantEvent, { passive: true });
    });

    bindActivationHooks();
  }

  // ---------- mutation observer ----------
  function flushMutationWork() {
    mutationFlushRaf = 0;
    if (!pendingMutationSectionIds.size) return;

    resetVisibleElementCache();
    pendingMutationSectionIds.forEach(function (sid) {
      if (sid) scheduleSync(sid);
    });
    pendingMutationSectionIds.clear();

    var openModal = getOpenProductModal();
    if (!openModal || !openModal.id || openModal.id.indexOf('ProductModal-') !== 0) return;
    scheduleFilter(openModal.id.replace('ProductModal-', ''));
  }

  var observer = new MutationObserver(function (muts) {
    if (!activationState.active) return;
    // Mutation volume can be high on product pages; wait until a modal is open before tracking silent gallery rewrites.
    if (!getCachedOpenProductModal()) return;

    var touched = false;
    muts.forEach(function (m) {
      var isRelevantMutation =
        (m.type === 'childList' && ((m.addedNodes && m.addedNodes.length) || (m.removedNodes && m.removedNodes.length))) ||
        (m.type === 'attributes' && m.attributeName === 'data-media-id');
      if (!isRelevantMutation) return;

      var src = m.target && m.target.closest && m.target.closest(GALLERY_SEL);
      if (!src || !src.id) return;
      touched = true;
      pendingMutationSectionIds.add(src.id.replace('MediaGallery-', ''));
    });

    if (!touched || mutationFlushRaf) return;
    mutationFlushRaf = requestAnimationFrame(flushMutationWork);
  });

  function observeGalleries() {
    if (!hasProductMediaContext()) return;

    document.querySelectorAll(GALLERY_SEL).forEach(function (gal) {
      if (observedGalleries.has(gal)) return;
      observedGalleries.add(gal);
      observer.observe(gal, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-media-id']
      });
    });
  }

  // ---------- fallback patch ----------
  function patchProductModalFallback() {
    if (!('customElements' in window)) return;

    customElements.whenDefined('product-modal').then(function () {
      try {
        var C = customElements.get('product-modal');
        if (!C || C.prototype.__pmrSafe) return;

        var origShow = C.prototype.show;
        var origHide = C.prototype.hide;
        var orig = C.prototype.showActiveMedia;
        if (typeof origShow === 'function') {
          C.prototype.show = function () {
            var result = origShow.apply(this, arguments);
            rememberOpenProductModal(this);
            return result;
          };
        }

        if (typeof origHide === 'function') {
          C.prototype.hide = function () {
            var result = origHide.apply(this, arguments);
            forgetOpenProductModal(this);
            return result;
          };
        }

        C.prototype.showActiveMedia = function () {
          try {
            return orig.apply(this, arguments);
          } catch (e) {
            // Fallback keeps third-party ID drift from breaking the modal entirely; it only runs after Dawn's method throws.
            var id = this.activatedMediaId;
            var target =
              (id && this.querySelector('[data-media-id="' + id + '"]')) ||
              (id && id.indexOf('-') === -1 && this.querySelector('[data-media-id$="-' + id + '"]')) ||
              this.querySelector('[data-media-id]');
            if (target) {
              this.querySelectorAll('[data-media-id].is-active').forEach(function (n) { n.classList.remove('is-active'); });
              target.classList.add('is-active');
              try { target.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (_) {}
            }
          }
        };

        C.prototype.__pmrSafe = true;
        observeProductModals();
      } catch (_) {}
    });
  }

  if (window.__RISE_PRODUCT_MEDIA_REINIT_TEST__) {
    window.__RISE_PRODUCT_MEDIA_REINIT_TEST__.helpers = {
      countGalleries: function () { return document.querySelectorAll(GALLERY_SEL).length; },
      filterModalToVisible: filterModalToVisible,
      getCachedOpenProductModal: getCachedOpenProductModal,
      mapToExistingModalId: mapToExistingModalId,
      normalizeImageKey: normalizeImageKey,
      rememberOpenProductModal: rememberOpenProductModal,
      resolveModalMediaId: resolveModalMediaId,
      resetVisibleElementCache: resetVisibleElementCache,
      activateProductMediaReinit: activateProductMediaReinit
    };
  }

  bindEventHandlers();
  patchProductModalFallback();
})();
